class AnalyticsServicePLPHelper extends AnalyticsService {
  constructor() {
    super();
    this.device = window.innerWidth < 768 ? "mobile" : "desktop";
    this.deviceSuffix = this.device === "mobile" ? "_mobile" : "";
    this.listId = this.getListId();
    this.listName = this.getListName();
    this.currency = this.getCurrency();
    this.productCards = document.querySelectorAll(
      "[data-a-target-id='plp-product-card']"
    );
    this.init();
  }

  // HELPERS
  getListName() {
    return (
      document.querySelector("div[data-collection]")?.dataset.collection ||
      "PLP"
    );
  }

  getListId() {
    return (
      document.querySelector("div[data-collection-id]")?.dataset.collectionId ||
      "unknown"
    );
  }

  // CORE FUNCTIONS
  dispatchListViewEvent() {
    let products = Array.from(
      document.querySelectorAll("[data-a-target-id='plp-product-card']")
    );
    if (!products.length) return;
    const event = this.buildAnalyticsEvent("analytics:viewItemList", {
      item_list_id: this.listId,
      item_list_name: this.listName,
      currency: this.currency,
      ecommerce: this.buildEcommerceData(products, this.currency),
    });

    document.dispatchEvent(event);
  }

  dispatchItemClickEvent(productCard) {
    const embedEl = productCard.closest("collection-embed");
    const embeddedCollection = embedEl
      ? embedEl.dataset.targetUrl?.replace("/collections/", "") || null
      : null;

    const event = this.buildAnalyticsEvent("analytics:selectItem", {
      item_list_id: this.listId,
      item_list_name: this.listName,
      currency: this.currency,
      handle: productCard.getAttribute("data-product-handle"),
      product_name: productCard.getAttribute("data-product-name"),
      embedded_collection: embeddedCollection,
      ecommerce: this.buildEcommerceData([productCard], this.currency),
    });

    document.dispatchEvent(event);
  }
  dispatchProductCardSwipeEvent(payload) {
    const event = this.buildAnalyticsEvent(
      `analytics:${payload.event_name}`,
      payload
    );
    document.dispatchEvent(event);
  }
  setupPLPNavTracking() {
    const navLinks = document.querySelectorAll("[data-a-target='plp-nav']");
    navLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        const event = this.buildAnalyticsEvent("analytics:plpNavClick", {
          page_location: window.location.pathname,
          handle: link.getAttribute("href"),
          text: link.textContent.trim(),
          device: this.device,
        });
        document.dispatchEvent(event);
      });
    });
  }
  initPLPScrollTracking() {
    const trackedPages = new Set();
    const totalPages = parseInt(
      document.querySelector("infinite-scroll")?.dataset?.pageSize || "1"
    );

    const observePages = () => {
      const pageGroups = document.querySelectorAll("[data-page]");
      const seenPages = new Set();

      pageGroups.forEach((card) => {
        const pageNum = parseInt(card.dataset.page);
        if (seenPages.has(pageNum) || trackedPages.has(pageNum)) return;
        seenPages.add(pageNum);

        // Find the first card of each page to use as the observation target
        const firstCardOfPage = document.querySelector(
          `[data-page="${pageNum}"]`
        );
        if (!firstCardOfPage) return;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !trackedPages.has(pageNum)) {
                trackedPages.add(pageNum);
                const isFinalPage = pageNum >= totalPages;
                const depthValue = isFinalPage ? "pfinal" : `p${pageNum}`;

                const event = this.buildAnalyticsEvent(
                  "analytics:plpScroll",
                  {
                    page_location: window.location.pathname,
                    plp_scroll_depth: depthValue,
                    collection_handle: this.listName,
                  }
                );
                document.dispatchEvent(event);
                observer.disconnect();
              }
            });
          },
          { threshold: 0.1 }
        );
        observer.observe(firstCardOfPage);
      });
    };

    // Observe initial pages
    observePages();

    // Re-observe when new pages are loaded via infinite scroll
    const gridContainer = document.querySelector(".product__grid_4.unique");
    if (gridContainer) {
      const mutationObserver = new MutationObserver(() => {
        observePages();
      });
      mutationObserver.observe(gridContainer, { childList: true });
    }
  }

  initPLPSelectSize() {
    const sizeOptions = document.querySelectorAll(
      "[data-a-target-id='plp-product-card'] .variant-option"
    );
    if (!sizeOptions.length) return;

    sizeOptions.forEach((option) => {
      option.addEventListener("click", () => {
        const card = option.closest("[data-a-target-id='plp-product-card']");
        if (!card) return;
        const event = this.buildAnalyticsEvent("analytics:plpSelectSize", {
          product_id: card.dataset.productId,
          product_name: card.dataset.productName,
          product_type: card.dataset.productType,
          product_size: option.textContent.trim(),
          variant_id: option.dataset.variantId,
          page_location: window.location.pathname,
          item_list_name: this.listName,
        });
        document.dispatchEvent(event);
      });
    });
  }

  initCustomBlockTracking() {
    const blocks = document.querySelectorAll(
      "[data-a-target-id='plp-custom-block']"
    );
    if (!blocks.length) return;

    blocks.forEach((block) => {
      const blockName = block.dataset.blockName || "";

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            observer.disconnect();
            const event = this.buildAnalyticsEvent(
              "analytics:customBlockView",
              {
                block_name: blockName,
                page_location: window.location.pathname,
              }
            );
            document.dispatchEvent(event);
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(block);

      const links = block.querySelectorAll("a[data-handle]");
      links.forEach((link) => {
        link.addEventListener("click", () => {
          const event = this.buildAnalyticsEvent(
            "analytics:customBlockClick",
            {
              block_name: blockName,
              page_location: window.location.pathname,
              handle: link.dataset.handle || "",
            }
          );
          document.dispatchEvent(event);
        });
      });
    });
  }

  initEmbedViewTracking() {
    const embeds = document.querySelectorAll("collection-embed");
    if (!embeds.length) return;

    embeds.forEach((embed) => {
      let fired = false;
      const targetHandle =
        embed.dataset.targetUrl?.replace("/collections/", "") || "unknown";
      const gridEl = embed.querySelector("[data-embed-grid]");
      if (!gridEl) return;

      const fireView = () => {
        if (fired) return;
        const products = Array.from(
          gridEl.querySelectorAll("[data-a-target-id='plp-product-card']")
        );
        if (!products.length) return;
        fired = true;

        const event = this.buildAnalyticsEvent("analytics:viewItemList", {
          item_list_id: targetHandle,
          item_list_name: targetHandle,
          embedded_collection: targetHandle,
          currency: this.currency,
          ecommerce: this.buildEcommerceData(products, this.currency),
        });
        document.dispatchEvent(event);
      };

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting || fired) return;
          if (gridEl.children.length) {
            fireView();
            observer.disconnect();
          } else {
            const mutation = new MutationObserver(() => {
              if (gridEl.children.length) {
                fireView();
                mutation.disconnect();
                observer.disconnect();
              }
            });
            mutation.observe(gridEl, { childList: true });
          }
        },
        { threshold: 0 }
      );
      observer.observe(embed);
    });
  }

  init() {
    // these are needed as the Product cards are dynamically loaded and cannot all be initialized at once
    document.addEventListener("analytics:PLPHelper:itemClick", (e) => {
      this.dispatchItemClickEvent(e.detail.productCard);
    });
    document.addEventListener(
      "analytics:PLPHelper:productCardSwipeEvent",
      (e) => {
        this.dispatchProductCardSwipeEvent(e.detail);
      }
    );
    this.dispatchListViewEvent();
    this.setupPLPNavTracking();
    this.initPLPScrollTracking();
    this.initPLPSelectSize();
    this.initCustomBlockTracking();
    this.initEmbedViewTracking();
  }
}
